import {
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GuidedTourService } from '../guided-tour.service';
import { Tutorial, TourViewState, TutorialPlacement } from '../tutorial.types';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Coordenadas calculadas para pintar un paso. */
interface Layout {
  spotlight: Rect | null; // null ⇒ paso centrado (sin recorte)
  card: { top: number; left: number };
  placement: TutorialPlacement;
}

// Ancho/alto TOTALES de la tarjeta (§20 T6.5): `.gt-card` declara
// `box-sizing: border-box`, así que su padding de 16px va POR DENTRO de estos
// 340px. Antes era `content-box` y la tarjeta medía 372px reales mientras el
// posicionamiento calculaba con 340 → se desviaba 32px (se salía por la derecha
// en anclas pegadas al borde). Si cambias `width`/`max-height` en el SCSS,
// cambia también estas constantes.
const CARD_W = 340;
const CARD_MAX_H = 320;
const GAP = 14; // separación entre el recorte y la tarjeta
const PAD = 6; // padding del recorte alrededor del elemento

// ── Botón de "Retomar tutorial" ──────────────────────────────────────────────
// Va a la ALTURA del toolbar del grafo y pegado al borde derecho. Como el
// toolbar es `absolute` dentro del lienzo (y el lienzo cuelga de la pestaña del
// caso), su `top` real en pantalla depende del header/tabs de arriba: no se
// puede clavar en el CSS, hay que medirlo.
const FAB_H = 32; // alto real del botón: padding 6 + icono 20 + padding 6
const FAB_TOP_FALLBACK = 84; // pantallas sin toolbar (bajo el header de 75px)
const FAB_RIGHT = 16;
// Barra flotante y paneles derechos de Immersion (SCG-362).
const TOOLBAR_SEL = '[data-tour="immersion.toolbar"]';
const DRAWER_SEL = '.side-drawer';

/**
 * Overlay del coach-mark (§3). Se monta UNA vez, alto en el árbol
 * (app.component), para flotar sobre cualquier módulo. Pinta un backdrop con
 * "agujero" (spotlight) sobre el ancla y una tarjeta paso-a-paso con
 * navegación. Reposiciona en scroll/resize. Sin librerías externas.
 */
@Component({
  selector: 'app-guided-tour-overlay',
  templateUrl: './guided-tour-overlay.component.html',
  styleUrls: ['./guided-tour-overlay.component.scss'],
})
export class GuidedTourOverlayComponent implements OnInit, OnDestroy {
  view: TourViewState | null = null;
  layout: Layout | null = null;
  bodyHtml: SafeHtml = '';

  /**
   * Tutorial PAUSADO y relevante para la pantalla actual (§19). Cuando existe y
   * no hay un tour activo, se pinta el FAB flotante abajo-derecha para retomarlo.
   */
  resumable: { tutorial: Tutorial; step: number } | null = null;

  /**
   * Tutorial OFRECIDO para esta pantalla (§20 T1). Sustituye al auto-lanzamiento:
   * en vez de tomar la pantalla en la 1ª visita, se pinta una píldora discreta
   * abajo-centro que el usuario acepta ("Ver recorrido"), descarta para siempre
   * ("No, gracias") o cierra para más tarde. null ⇒ nada que ofrecer.
   */
  invitation: Tutorial | null = null;

  /** Tooltip del FAB: retomar es opcional (§20 T4 — antes sonaba a obligación). */
  readonly resumeTooltip =
    'Retomar el tutorial donde lo dejaste (opcional). Puedes descartarlo en tu perfil.';

  /**
   * Posición del botón de "Retomar": a la altura del toolbar del grafo y contra
   * el borde derecho de la pantalla. Se mide en vivo (`trackFab`) porque el
   * toolbar puede montar tarde (cambio de pestaña dentro del caso) y porque al
   * abrir un drawer derecho hay que apartarse para no tapar su cabecera.
   */
  fabTop = FAB_TOP_FALLBACK;
  fabRight = FAB_RIGHT;

  /** Tarjeta del paso actual: acota el teclado a su interior (§20 T6.2). */
  @ViewChild('gtCard') private cardRef?: ElementRef<HTMLElement>;

  /**
   * ¿Hay algún diálogo de la app abierto? Oculta el FAB de retomar (§19) mientras
   * dure (mejoras_8 T5). El FAB es `position: fixed` con el z-index tope del
   * overlay del tour, así que quedaba POR ENCIMA de `.cdk-overlay-container` y
   * tapaba "la mayoría de ventanas". No se le baja el z-index a propósito: la
   * tarjeta del tutorial SÍ debe seguir viéndose por encima de todo.
   */
  hayModalAbierto = false;

  private currentPath = '';
  private sub = new Subscription();
  private rafPending = false;
  /** Sondeo de la posición del botón de retomar (solo mientras se ve). */
  private fabTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * ¿Ya sincronizamos el interruptor MAESTRO (admin, §19.11) desde el backend
   * en esta sesión? El flag `tutorialsEnabled` viaja en el login, pero el GET
   * de sesión llega después de que arranque el servicio de progreso, que hasta
   * entonces asume ENCENDIDO. Sin
   * este GET, el apagado del admin no llegaba a los usuarios normales. Se pide
   * una vez por sesión (config liviana, no consume créditos) en cuanto hay
   * token; al cerrar sesión (sin token) se rearma para re-sincronizar al
   * próximo login.
   */
  private adminSynced = false;
  /** Estado ya traído del back en esta sesión (ver `syncProgressOnce`). */
  private progressSync: Promise<void> | null = null;

  constructor(
    public tour: GuidedTourService,
    private sanitizer: DomSanitizer,
    private zone: NgZone,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.currentPath = this.router.url.split('?')[0];
    // Trae el estado real del interruptor MAESTRO del admin (para TODOS los
    // usuarios, no solo el admin en su perfil). Sin esto, el apagado por
    // organización nunca llegaba a los usuarios normales.
    this.syncAdminEnabledOnce();
    this.syncProgressOnce();
    this.recomputeResumable();
    this.startFabTracking();
    this.watchOpenDialogs();

    this.sub.add(
      this.tour.observe().subscribe((v) => {
        this.view = v;
        this.bodyHtml = v
          ? this.sanitizer.bypassSecurityTrustHtml(v.step.body)
          : '';
        // Al terminar/pausar un tour puede aparecer un pendiente que retomar.
        this.recomputeResumable();
        // Espera un frame a que el DOM/scroll se asiente antes de medir.
        this.scheduleRecalc();
      }),
    );

    // 1ª visita a cada módulo (§11, revisado §20 T1): al terminar cada
    // navegación, si hay un tutorial con routeMatch que coincide y su estado es
    // notDone, se OFRECE con una píldora — ya no se lanza solo.
    this.sub.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => {
          this.currentPath = e.urlAfterRedirects.split('?')[0];
          // El login navega tras autenticar: es el momento en que aparece el
          // token, así que aquí sincronizamos el interruptor maestro.
          this.syncAdminEnabledOnce();
          this.syncProgressOnce();
          // La invitación pertenece a la pantalla que se abandona.
          this.invitation = null;
          this.recomputeResumable();
          this.maybeOffer(e.urlAfterRedirects);
        }),
    );

    // Invitaciones pedidas por el MOTOR (§20 T2/T5): abrir la tab de Inmersión
    // o completar un recorrido con cadena ya no lanzan nada — ofrecen. El canal
    // es el mismo que el de la 1ª visita por ruta, así que la píldora es siempre
    // la misma pieza de UI.
    this.sub.add(
      this.tour.observeOffers().subscribe((id) => {
        // `null` = invitación consumida (aceptada, descartada o aplazada).
        // Hay que limpiarla aquí también: `finishAndChain()` consume la oferta
        // que `finish()` acababa de emitir, y si no la borráramos la píldora
        // reaparecería al terminar el tutorial encadenado.
        this.invitation = id ? this.tour.getById(id) ?? null : null;
      }),
    );

    // El estado local (pausar/desactivar) cambia el FAB en vivo.
    this.sub.add(
      this.tour.observeLocal().subscribe(() => this.recomputeResumable()),
    );
    // El interruptor MAESTRO del admin también apaga/enciende el FAB (§19.11).
    this.sub.add(
      this.tour.observeAdminEnabled().subscribe(() => this.recomputeResumable()),
    );
  }

  /**
   * Sincroniza (una vez por sesión) el interruptor MAESTRO del admin desde el
   * backend. Solo con token (usuario autenticado); sin token se rearma para
   * volver a pedirlo tras el próximo login. Es un GET de configuración liviano
   * (no consume créditos).
   */
  private syncAdminEnabledOnce(): void {
    const hasToken = !!(
      localStorage.getItem('token') || sessionStorage.getItem('token')
    );
    if (!hasToken) {
      this.adminSynced = false; // logout ⇒ re-sincronizar al próximo login
      return;
    }
    if (this.adminSynced) return;
    this.adminSynced = true;
    this.sub.add(this.tour.refreshAdminEnabled().subscribe());
  }

  /**
   * Trae (una vez por sesión) el estado de tutoriales del back: el mapa de
   * progreso Y el estado del asistente (pausados, interruptor por-usuario,
   * enfriamientos). Es la ÚNICA hidratación — el cliente no guarda nada en el
   * navegador— y por eso `maybeOffer` espera esta promesa antes de ofrecer
   * nada: sin ella un recorrido ya terminado se volvería a ofrecer.
   *
   * Mismo patrón que `syncAdminEnabledOnce`: solo con token, y se rearma tras
   * el logout. Es un GET liviano y NO consume créditos.
   */
  private syncProgressOnce(): void {
    const hasToken = !!(
      localStorage.getItem('token') || sessionStorage.getItem('token')
    );
    if (!hasToken) {
      // Logout: además de re-sincronizar al próximo login, se vacía el estado
      // en memoria (doLogout no recarga la página).
      if (this.progressSync) this.tour.resetSession();
      this.progressSync = null;
      return;
    }
    if (this.progressSync) return;
    this.progressSync = new Promise<void>((resolve) => {
      this.sub.add(
        this.tour.refreshProgress().subscribe({
          next: () => resolve(),
          error: () => resolve(),
        }),
      );
    });
  }

  /**
   * Vigila los diálogos abiertos de la app para esconder el FAB mientras haya
   * alguno (mejoras_8 T5). `MatDialog` es la fuente de verdad de Material y
   * cubre TODOS los diálogos (resultados de búsqueda, crear nodo, editar,
   * confirmaciones…), así que no hace falta un `MutationObserver` sobre el DOM.
   *
   * No hay riesgo de esconderlo por culpa del propio tutorial: el overlay del
   * tour no es un `MatDialog` y, además, el FAB solo se pinta con `!view` (sin
   * tour activo).
   */
  private watchOpenDialogs(): void {
    this.syncModalAbierto();
    this.sub.add(
      this.dialog.afterOpened.subscribe(() => this.syncModalAbierto()),
    );
    // `afterAllClosed` emite solo al cerrarse el ÚLTIMO diálogo, que es justo lo
    // que queremos: con uno todavía abierto el FAB debe seguir oculto.
    this.sub.add(
      this.dialog.afterAllClosed.subscribe(() => this.syncModalAbierto()),
    );
  }

  /** Refresca el flag DENTRO de la zona: el CDK puede emitir fuera de ella y
   *  entonces el `*ngIf` del FAB no se repintaría. */
  private syncModalAbierto(): void {
    this.zone.run(() => {
      this.hayModalAbierto = this.dialog.openDialogs.length > 0;
    });
  }

  /** Recalcula si hay un tutorial pausado que ofrecer retomar (FAB). */
  private recomputeResumable(): void {
    // Con un tour activo ya se ve la tarjeta; el FAB solo aparece sin tour.
    this.resumable = this.tour.isActive()
      ? null
      : this.tour.getResumable(this.currentPath);
    if (this.resumable) this.syncFabPosition();
  }

  // ── Posición del botón de "Retomar" ────────────────────────────────────────

  /**
   * Mide dónde debe ir el botón: alineado verticalmente con el centro del
   * toolbar del grafo y contra el borde derecho, apartándose si hay un drawer
   * derecho abierto (su cabecera lleva la × justo a esa altura). Sin toolbar en
   * pantalla (perfil, listados, …) usa el respaldo bajo el header.
   */
  private measureFab(): { top: number; right: number } {
    const tb = document
      .querySelector<HTMLElement>(TOOLBAR_SEL)
      ?.getBoundingClientRect();
    const top =
      tb && tb.height
        ? Math.max(8, Math.round(tb.top + (tb.height - FAB_H) / 2))
        : FAB_TOP_FALLBACK;

    const drawer = document
      .querySelector<HTMLElement>(DRAWER_SEL)
      ?.getBoundingClientRect();
    const right =
      drawer && drawer.width
        ? Math.round(window.innerWidth - drawer.left + 12)
        : FAB_RIGHT;

    return { top, right };
  }

  private syncFabPosition(): void {
    const { top, right } = this.measureFab();
    this.fabTop = top;
    this.fabRight = right;
  }

  /**
   * Sondeo de VISIBILIDAD y posición del botón de retomar. Es un intervalo y no
   * un `ResizeObserver`/evento a propósito: lo que lo cambia son montajes de
   * OTROS componentes —cambiar de pestaña dentro del caso monta o desmonta el
   * grafo, abrir un drawer corre el borde derecho— y eso no genera navegación
   * ni resize. Sin este sondeo el botón se quedaba pegado en pestañas donde su
   * recorrido no tiene nada que señalar (`requiresAnchor` no se re-evaluaba).
   *
   * Corre FUERA de la zona y solo entra a ella cuando algo cambió de verdad, así
   * que en reposo no dispara detección de cambios.
   */
  private startFabTracking(): void {
    if (this.fabTimer) return;
    this.zone.runOutsideAngular(() => {
      this.fabTimer = setInterval(() => {
        const next = this.tour.isActive()
          ? null
          : this.tour.getResumable(this.currentPath);
        const { top, right } = next
          ? this.measureFab()
          : { top: this.fabTop, right: this.fabRight };
        const mismoTutorial =
          next?.tutorial.id === this.resumable?.tutorial.id &&
          next?.step === this.resumable?.step;
        if (mismoTutorial && top === this.fabTop && right === this.fabRight) {
          return;
        }
        this.zone.run(() => {
          this.resumable = next;
          this.fabTop = top;
          this.fabRight = right;
        });
      }, 600);
    });
  }

  private stopFabTracking(): void {
    if (this.fabTimer) {
      clearInterval(this.fabTimer);
      this.fabTimer = null;
    }
  }

  /** Retoma el tutorial pausado en el paso donde quedó. */
  resumeTutorial(): void {
    if (this.resumable) this.tour.resume(this.resumable.tutorial.id);
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.stopFabTracking();
  }

  /**
   * OFRECE (no lanza) el tutorial de la pantalla en la 1ª visita (§20 T1). El
   * criterio de elegibilidad es el mismo de antes (`shouldAutoStart`: notDone, no
   * pausado, tutoriales encendidos) más el enfriamiento persistente de §20 T6.4
   * (`canOffer`); lo único que cambia es el resultado — una píldora que el
   * usuario acepta o descarta, en vez de un recorrido encima de su trabajo.
   */
  private async maybeOffer(url: string): Promise<void> {
    if (this.tour.isActive()) return;
    const path = url.split('?')[0];
    // Nada se ofrece antes de saber qué vio ya el usuario: decidir con el mapa
    // todavía vacío es justamente lo que reofrecía recorridos terminados.
    await this.progressSync;
    // …y si esa hidratación FALLÓ (red caída, back sin estos endpoints), el mapa
    // vacío no dice "no lo ha visto" sino "no lo sabemos": callar es lo correcto.
    if (!this.tour.isProgressHydrated()) return;
    // Espera a que la pantalla se asiente (y monten las anclas de
    // `requiresAnchor`): la píldora no debe aparecer sobre una vista a medio
    // cargar ni durante un rebote de navegación.
    setTimeout(() => {
      if (this.currentPath !== path) return; // el usuario ya se fue
      const candidate = this.tour.findOffer(path);
      if (candidate) this.tour.offer(candidate.id); // vuelve por `observeOffers()`
    }, 900);
  }

  /** Duración estimada del recorrido ofrecido (≈15 s por paso), para la píldora. */
  get invitationMinutes(): number {
    const pasos = this.invitation?.steps.length ?? 0;
    return Math.max(1, Math.round((pasos * 15) / 60));
  }

  /** "Ver recorrido": el usuario acepta la invitación (§20 T1). */
  acceptInvitation(): void {
    const t = this.invitation;
    if (!t) return;
    this.invitation = null;
    this.tour.consumeOffer(t.id);
    this.tour.start(t.id);
  }

  /** "No, gracias": descarta el tutorial para siempre (reactivable en el perfil). */
  declineInvitation(): void {
    const t = this.invitation;
    if (!t) return;
    this.invitation = null;
    this.tour.consumeOffer(t.id);
    this.tour.dismiss(t.id);
  }

  /**
   * Cierra la píldora sin decidir ("más tarde"). Con el enfriamiento persistente
   * (§20 T6.4) esto ya significa algo entre sesiones: no se vuelve a ofrecer
   * hasta pasados 7 días (antes cada F5 la traía de vuelta).
   */
  closeInvitation(): void {
    const t = this.invitation;
    this.invitation = null;
    if (t) this.tour.consumeOffer(t.id);
  }

  /**
   * "No mostrar más" desde la tarjeta del tour (§20 T4): descarta el tutorial
   * activo en vez de dejarlo pausado con el FAB pidiendo que se retome.
   */
  dismissForever(): void {
    const id = this.view?.tutorial.id;
    if (!id) return;
    this.tour.dismiss(id); // cierra el tour activo y lo marca `skipped`
  }

  get isFirst(): boolean {
    return !!this.view && this.view.index === 0;
  }

  get isLast(): boolean {
    return !!this.view && this.view.index === this.view.total - 1;
  }

  /**
   * Texto del botón primario. En el último paso es SIEMPRE "Finalizar" (§20 T5):
   * antes mostraba el `chainLabel`, así que el gesto natural de cerrar ("el botón
   * azul de la derecha") arrastraba al usuario a otro recorrido entero. El
   * encadenado vive ahora en el botón secundario (`chainOffer`).
   */
  get primaryLabel(): string {
    if (!this.view || !this.isLast) return 'Siguiente';
    return 'Finalizar';
  }

  /**
   * Tutorial encadenado ofrecido en el último paso, como acción SECUNDARIA
   * (§20 T5). null ⇒ no hay cadena, está apagada o el usuario la descartó.
   */
  get chainOffer(): { id: string; label: string } | null {
    if (!this.isLast) return null;
    return this.tour.getChainOffer();
  }

  /** Botón secundario "Continuar con X": completar y seguir, porque lo pidió. */
  continueChain(): void {
    this.tour.finishAndChain();
  }

  /**
   * ¿El paso actual sugiere una acción real y la está esperando? (gating suave,
   * §20 T3). Si es así se muestra `confirmHint` COMO SUGERENCIA, junto al botón
   * "Siguiente" — que ya no se oculta: hacer la acción adelanta el paso solo,
   * pero no es requisito para continuar.
   */
  get isAwaitingConfirm(): boolean {
    return this.tour.isAwaitingConfirm();
  }

  get confirmHint(): string {
    return (
      this.view?.step.confirmHint ||
      'Realiza la acción indicada para continuar.'
    );
  }

  /**
   * ¿Este paso BLOQUEA el clic fuera del hueco? (§20 T6.1 — default invertido.)
   * Antes las máscaras capturaban todo salvo permiso explícito, y cada paso se
   * sentía una cárcel. Ahora se puede interactuar con toda la pantalla y solo
   * bloquean donde clicar fuera destruiría el contexto del propio paso (los
   * pasos dentro de un `mat-menu`, que se cierra con cualquier clic al backdrop).
   */
  get blocksOutside(): boolean {
    return !!this.view?.step.blockOutsideInteraction;
  }

  next(): void {
    this.tour.next();
  }

  prev(): void {
    this.tour.prev();
  }

  /** Salir del tutorial: NO lo descarta — lo PAUSA y recuerda el paso (§19). */
  exit(): void {
    this.tour.pause();
  }

  @HostListener('window:scroll', ['$event'])
  @HostListener('window:resize', ['$event'])
  onViewportChange(): void {
    this.scheduleRecalc();
    if (this.resumable) this.syncFabPosition();
  }

  /**
   * Teclado ACOTADO (§20 T6.2). Antes el tutorial se quedaba con Enter, las
   * flechas y Escape de TODA la app mientras estaba activo — en una aplicación
   * de grafo esas teclas tienen dueño. Ahora:
   *  - `Escape` sigue siendo global (es la salida universal y no compite con
   *    nada mientras hay un coach-mark encima),
   *  - `Enter`/`←`/`→` solo cuando el foco está DENTRO de la tarjeta del paso.
   */
  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (!this.view) return;
    // Con el tour interactivo el usuario puede escribir en inputs reales (p. ej.
    // la caja del chat). Si el foco está en un campo editable, NO secuestramos
    // las teclas: Enter debe enviar el mensaje / mover el cursor.
    if (this.isEditableTarget(e.target)) return;
    if (e.key === 'Escape') {
      this.exit();
      return;
    }
    if (!this.isInsideCard(e.target)) return;
    if (e.key === 'ArrowRight' || e.key === 'Enter') {
      this.next();
    } else if (e.key === 'ArrowLeft') {
      this.prev();
    }
  }

  /** ¿El evento nace dentro de la tarjeta del paso? (navegación por teclado) */
  private isInsideCard(target: EventTarget | null): boolean {
    const card = this.cardRef?.nativeElement;
    return !!card && !!target && card.contains(target as Node);
  }

  /** ¿El evento viene de un campo donde el usuario está escribiendo? */
  private isEditableTarget(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el || !el.tagName) return false;
    const tag = el.tagName.toLowerCase();
    return (
      tag === 'input' ||
      tag === 'textarea' ||
      tag === 'select' ||
      el.isContentEditable === true
    );
  }

  private scheduleRecalc(): void {
    if (this.rafPending) return;
    this.rafPending = true;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.rafPending = false;
        this.zone.run(() => (this.layout = this.computeLayout()));
        // Segundo frame: con la tarjeta ya pintada se puede medir su ALTO REAL
        // y corregir si se sale de la pantalla (ver `fitCardToViewport`).
        requestAnimationFrame(() => this.zone.run(() => this.fitCardToViewport()));
      });
    });
  }

  /**
   * Corrige la posición de la tarjeta con su tamaño REAL, ya pintada.
   *
   * `placeCard` posiciona a ciegas con `CARD_MAX_H` y luego recortaba con
   * `top ≤ vh - 120`, lo que dejaba hasta 200px de tarjeta fuera de la pantalla:
   * los pasos anclados abajo (los del asistente Forsker, cuyas anclas viven en
   * el panel del chat pegado al borde inferior) aparecían cortados y sin poder
   * llegar a los botones. Como la tarjeta es más baja que el máximo casi
   * siempre, medirla permite subirla lo justo en vez de asumir el peor caso.
   */
  private fitCardToViewport(): void {
    const el = this.cardRef?.nativeElement;
    const l = this.layout;
    if (!el || !l) return;
    const h = el.offsetHeight;
    const w = el.offsetWidth;
    const top = Math.min(
      Math.max(12, l.card.top),
      Math.max(12, window.innerHeight - h - 12),
    );
    const left = Math.min(
      Math.max(12, l.card.left),
      Math.max(12, window.innerWidth - w - 12),
    );
    if (top === l.card.top && left === l.card.left) return;
    this.layout = { ...l, card: { top, left } };
  }

  private computeLayout(): Layout | null {
    if (!this.view) return null;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Paso centrado: sin recorte, tarjeta al centro.
    if (!this.view.target) {
      return {
        spotlight: null,
        placement: 'center',
        card: {
          top: Math.max(24, vh / 2 - CARD_MAX_H / 2),
          left: Math.max(16, vw / 2 - CARD_W / 2),
        },
      };
    }

    const r = this.view.target.getBoundingClientRect();
    const spot: Rect = {
      top: r.top - PAD,
      left: r.left - PAD,
      width: r.width + PAD * 2,
      height: r.height + PAD * 2,
    };

    const placement = this.pickPlacement(spot, vw, vh);
    const card = this.placeCard(spot, placement, vw, vh);
    return { spotlight: spot, placement, card };
  }

  /** Elige el lado con más espacio; preferencia del paso si cabe. */
  private pickPlacement(
    spot: Rect,
    vw: number,
    vh: number,
  ): TutorialPlacement {
    const pref = this.view?.step.placement;
    const fits = (p: TutorialPlacement): boolean => {
      switch (p) {
        case 'bottom':
          return vh - (spot.top + spot.height) > CARD_MAX_H + GAP;
        case 'top':
          return spot.top > CARD_MAX_H + GAP;
        case 'right':
          return vw - (spot.left + spot.width) > CARD_W + GAP;
        case 'left':
          return spot.left > CARD_W + GAP;
        default:
          return true;
      }
    };
    if (pref && pref !== 'center' && fits(pref)) return pref;
    for (const p of ['bottom', 'top', 'right', 'left'] as TutorialPlacement[]) {
      if (fits(p)) return p;
    }
    return 'center';
  }

  private placeCard(
    spot: Rect,
    placement: TutorialPlacement,
    vw: number,
    vh: number,
  ): { top: number; left: number } {
    let top = 0;
    let left = 0;
    switch (placement) {
      case 'bottom':
        top = spot.top + spot.height + GAP;
        left = spot.left + spot.width / 2 - CARD_W / 2;
        break;
      case 'top':
        top = spot.top - GAP - CARD_MAX_H;
        left = spot.left + spot.width / 2 - CARD_W / 2;
        break;
      case 'right':
        top = spot.top + spot.height / 2 - CARD_MAX_H / 2;
        left = spot.left + spot.width + GAP;
        break;
      case 'left':
        top = spot.top + spot.height / 2 - CARD_MAX_H / 2;
        left = spot.left - GAP - CARD_W;
        break;
      default:
        top = vh / 2 - CARD_MAX_H / 2;
        left = vw / 2 - CARD_W / 2;
    }
    // Clamp al viewport.
    left = Math.min(Math.max(12, left), vw - CARD_W - 12);
    top = Math.min(Math.max(12, top), vh - 120);
    return { top, left };
  }
}
